import classNames from "classnames";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useMyCarDocumentsApi } from "../../http/carDocuments/api";
import { CarDocument } from "../../http/carDocuments/types";
import { useCarsApi } from "../../http/cars/api";
import { getFuelTypes, getTypesOfUse } from "../../http/cars/types";
import { useAdminClaimsApi } from "../../http/claims/api";
import { ClaimNoCarDependency, getClaimStateColor, getClaimStatuses } from "../../http/claims/types";
import { useMyCarDueDatesApi } from "../../http/dueDates/api";
import { DueDateNoCarDependency } from "../../http/dueDates/types";
import { useMyCarMileagesApi } from "../../http/mileages/api";
import { MileageNoCarDependency, UpsertMileage } from "../../http/mileages/types";
import { useRentalCarApi } from "../../http/rentalCars/api";
import { RentalCarNoCarDepency } from "../../http/rentalCars/types";
import { useMyCarWorkflowsApi } from "../../http/workflow/api";
import { Workflow } from "../../http/workflow/types";
import { useMyCarWorkflowAttachmentsApi } from "../../http/workflowAttachments/api";
import { NestedPick } from "../../types";
import {
  convertKilometersToMiles,
  convertMilesToKilometers,
  formatDateNumeric,
  formatTimeStampNumeric,
  isOutPrevious7days,
} from "../../types/utils";
import SvgAttach from "../icons/Attach";
import SvgAutoRenew from "../icons/AutoRenew";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgCheck from "../icons/CheckIcon";
import SvgDoNotDisturbOn from "../icons/DoNotDisturbOn";
import SvgHourglass from "../icons/Hourglass";
import SvgUpload from "../icons/Upload";
import SvgWarning from "../icons/Warning";
import DeleteMileageDialog from "../mileages/DeleteMileageDialog";
import EditMileageDialog from "../mileages/EditMileageDialog";
import MileageForm, { MileageFormBody } from "../mileages/MileageForm";
import Box from "../ui/Box";
import { Check } from "../ui/Check";
import CustomLink from "../ui/CustomLink";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import FileUploader from "../ui/FileUploader";
import Header from "../ui/Header";
import Icon from "../ui/Icon";
import { getDeleteAction, getDownloadAction, getEditAction } from "../ui/ListItemMenu";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import MyCarDueDateAttachments from "./MyCarDueDateAttachments";
import styles from "./MyCarOverview.module.css";
import OverviewGrid from "./OverviewGrid";
import RenewDueDatesDialog from "./RenewUserDueDateDialog";

const WORKFLOW_FIELDS = [
  "id",
  "createdAt",
  "author",
  "author.id",
  "author.fullName",
  "driver",
  "driver.id",
  "driver.fullName",
  "carTask",
  "carTask.id",
  "carTask.taskName",
  "carTask.defaultTask",
  "carTask.defaultTask.id",
  "carTask.defaultTask.name",
  "stickers",
  "stickers.state",
  "state",
] as const;
type WorkflowField = (typeof WORKFLOW_FIELDS)[number];
type WorkflowSearch = NestedPick<Workflow, WorkflowField>;

const MILEAGE_FIELDS = ["id", "date", "distance", "createdAt"] as const;
type MileageField = (typeof MILEAGE_FIELDS)[number];
type MileageSearch = NestedPick<MileageNoCarDependency, MileageField>;

const DUE_DATE_FIELDS = ["id", "date", "type.name", "visibleToUser", "isSettled", "hasAttachments"] as const;
type DueDateField = (typeof DUE_DATE_FIELDS)[number];
type DueDateSearch = NestedPick<DueDateNoCarDependency, DueDateField>;

const RENTAL_CAR_FIELDS = ["id", "startDate", "endDate", "plateNumber"] as const;
type RentalCarField = (typeof RENTAL_CAR_FIELDS)[number];
type RentalCarSearch = NestedPick<RentalCarNoCarDepency, RentalCarField>;

const CLAIM_FIELDS = ["id", "number", "status", "type.id", "type.name", "incidentDate", "openingDate"] as const;
type ClaimField = (typeof CLAIM_FIELDS)[number];
type ClaimSearch = NestedPick<ClaimNoCarDependency, ClaimField>;

interface OverviewMyCarProps {
  isTeam?: boolean;
}

const empty = "---";

const OverviewMyCar: FC<OverviewMyCarProps> = ({ isTeam }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const [openNewMileage, setOpenNewMileage] = useState(false);
  const [mileageToEdit, setMileageToEdit] = useState<MileageSearch>();
  const [mileageToDelete, setMileageToDelete] = useState<MileageSearch>();
  const [workflowToUpdate, setWorkflowToUpdate] = useState<WorkflowSearch>();
  const [isSubmittingUpload, setIsSubmittingUpload] = useState<boolean>(false);
  const [showRenewDueDate, setShowRenewDueDate] = useState<DueDateSearch>();
  const [showDueDateAttachments, setShowDueDateAttachments] = useState<DueDateSearch>();

  const { carId } = useParams<{ carId: string }>();
  const { carDriverId } = useParams<{ carDriverId: string }>();

  const mileagesApi = useMyCarMileagesApi();
  const carsApi = useCarsApi();
  const dueDateApi = useMyCarDueDatesApi();
  const workflowsApi = useMyCarWorkflowsApi();
  const workflowAttachmentsApi = useMyCarWorkflowAttachmentsApi();
  const claimsApi = useAdminClaimsApi();
  const carDocumentsApi = useMyCarDocumentsApi();
  const api = useApi();

  const workflows = workflowsApi.useWorkflows(
    {
      limit: 10,
      desc: true,
      fields: WORKFLOW_FIELDS,
    },
    aborter.current.signal,
    carId!,
  );
  const [initializedWorkflows, isWorkflowsInitialized] = useDataInitializer(workflows.data, workflows.error);

  const param = new URLSearchParams(window.location.search);

  const addMileage = param.get("add_mileage") == "1";
  useEffect(() => {
    if (addMileage) {
      setOpenNewMileage(true);
    }
  }, [addMileage]);

  const closeNewMileageDialog = () => {
    setOpenNewMileage(false);
    if (addMileage) {
      const url = new URL(window.location.href);
      url.searchParams.delete("add_mileage");
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  const wf_id = param.get("wf_id");
  useEffect(() => {
    if (wf_id && isWorkflowsInitialized) {
      const workflow = initializedWorkflows?.items.find((wf) => wf.id === wf_id);
      workflow?.state === "pending" && setWorkflowToUpdate(workflow);
    }
  }, [wf_id, isWorkflowsInitialized]);

  const closeWorkflowDialog = () => {
    setWorkflowToUpdate(undefined);
    if (wf_id) {
      const url = new URL(window.location.href);
      url.searchParams.delete("wf_id");
      window.history.replaceState({}, document.title, url.toString());
    }
  };
  const rentalCarsApi = useRentalCarApi();

  const carSwr = carsApi.useMyCar(carId!, carDriverId!, aborter.current.signal);
  const [car, isInitialized] = useDataInitializer(carSwr.data, carSwr.error);

  const mileages = mileagesApi.useCarMileages(
    {
      limit: 5,
      desc: true,
      fields: MILEAGE_FIELDS,
    },
    aborter.current.signal,
    carId!,
    carDriverId!,
  );
  const [initializedMileages] = useDataInitializer(mileages.data, mileages.error);

  const dueDates = dueDateApi.useMyCarDueDates(
    {
      limit: 10,
      desc: true,
      fields: DUE_DATE_FIELDS,
    },
    aborter.current.signal,
    carId!,
    isTeam ?? false,
  );
  const [initializedDueDates] = useDataInitializer(dueDates.data, dueDates.error);

  const rentalCars = rentalCarsApi.useAdminRentalCar({ fields: RENTAL_CAR_FIELDS }, aborter.current.signal, carId);
  const [initializedRentals] = useDataInitializer(rentalCars.data, rentalCars.error);

  const claims = claimsApi.useAdminClaims(
    { limit: 10, desc: true, fields: CLAIM_FIELDS },
    aborter.current.signal,
    carId,
  );
  const [initializedClaims] = useDataInitializer(claims.data, claims.error);

  const carDocuments = carDocumentsApi.useMyCarDocuments(
    { limit: 10 },
    aborter.current.signal,
    carId!,
    isTeam ?? false,
  );
  const [initializedCarDocuments] = useDataInitializer(carDocuments.data, carDocuments.error);

  useEffect(() => {
    (carSwr.error || mileages.error) && dispatch(showErrorNotification(i18n, carSwr.error ?? mileages.error));
  }, [carSwr.error, mileages.error]);

  const fuelTypes = getFuelTypes(i18n.t);

  const onSubmitInsertMileage = async (formData: MileageFormBody): Promise<void> => {
    if (carDriverId && carId) {
      const body: UpsertMileage = {
        carId: carId,
        date: formData.date ?? "",
        distance: formData.distance
          ? !!userSettings.me.distanceInMiles
            ? convertMilesToKilometers(formData.distance)
            : formData.distance
          : 0,
      };

      try {
        await mileagesApi.insertMileage(body, carDriverId);
        dispatch(showSuccessNotification(i18n.t));
        closeNewMileageDialog();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  const onSubmitUploadImages = async (files: File[]): Promise<void> => {
    setIsSubmittingUpload(true);
    if (!!workflowToUpdate) {
      try {
        await workflowAttachmentsApi.insertWorkflowAttachments(files, workflowToUpdate.id, carId);
        dispatch(showSuccessNotification(i18n.t));
        setWorkflowToUpdate(undefined);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
    setIsSubmittingUpload(false);
  };

  const getCarDocumentUrl = (item: CarDocument) =>
    `/api/${isTeam ? "my_team_cars" : "my_cars"}/car_documents/${item.id}`;

  const downloadCarDocument = (item: CarDocument) =>
    api.downloadFile(getCarDocumentUrl(item), item.name).catch((err) => dispatch(showErrorNotification(i18n, err)));

  const showNewMileageButton = () => {
    if (isTeam || !car?.driver) {
      return false;
    }
    if (!car?.driver?.endDate) {
      return true;
    }

    const endDate = new Date(car.driver.endDate);
    const today = new Date();

    // Calculate today + 7 days
    const plus7Days = new Date(today);
    plus7Days.setDate(today.getDate() + 7);

    // Get latest mileage date
    // const lastMileage = initializedMileages?.items.reduce((latest, m) => {
    //   // console.log(`${latest} : ${m.date} -> ${m.date.localeCompare(latest)}`);
    //   return m.date.localeCompare(latest) > 0 ? m.date : latest;
    // }, "");

    // const lastMileageDate = lastMileage ? new Date(lastMileage + "T00:00:00") : null;

    // if the end date is not more than a week in the past and the last mileage isnt the last of drivers period
    if (
      plus7Days < endDate
      // && !sameDay(lastMileageDate, endDate)
    ) {
      return true;
    }

    return false;
  };

  const getIconWorkflow = (item: WorkflowSearch) => {
    const d = new Date(item.createdAt);
    d?.setDate(d.getDate() + 10);
    const maxDate = d;
    const today = new Date();

    return !item.state ? (
      ""
    ) : item.state == "pending" ? (
      <div title={i18n.t.common.uploadImagesRequired}>
        <Icon
          glyph={SvgWarning}
          className={classNames(styles.icon, maxDate && maxDate < today ? styles.iconRed : styles.iconYellow)}
        />
      </div>
    ) : item.state == "closed" ? (
      <div title={item.stickers ? i18n.t.common.imagesAccepted : i18n.t.common.closed}>
        <Icon glyph={SvgCheckCircle} className={classNames(styles.icon, styles.iconGreen)} />
      </div>
    ) : item.state == "image-supplied" ? (
      <div title={i18n.t.common.waitingForImageApproval}>
        <Icon glyph={SvgHourglass} className={classNames(styles.icon)} />
      </div>
    ) : item.state == "cancelled" ? (
      <div title={item.state}>
        <Icon glyph={SvgDoNotDisturbOn} className={classNames(styles.icon, styles.iconGrey)} />
      </div>
    ) : (
      item.state
    );
  };

  const getActionText = (item: WorkflowSearch) => {
    if (item.stickers) {
      return i18n.t.common.uploadImages;
    }

    return "";
  };

  return (
    <>
      <Header title={isTeam ? i18n.t.common.teamCar : i18n.t.common.myCar} />
      <Panel withBorder={false}>
        <OverviewGrid>
          <div>
            <Box.Group
              boxs={[
                <Box
                  title={i18n.t.common.generalData}
                  fields={[
                    [
                      {
                        title: i18n.t.common.vehicleIdentificationNumber,
                        data: car?.vehicleIdentificationNumber ? car?.vehicleIdentificationNumber : empty,
                      },
                      {
                        title: i18n.t.common.registrationDate,
                        data: car?.registrationDate ? formatDateNumeric(car?.registrationDate, i18n.language) : empty,
                      },
                    ],
                    [
                      {
                        title: i18n.t.subsidiaries.singular,
                        data: car?.subsidiary ? car?.subsidiary : empty,
                      },
                      {
                        title: i18n.t.manufacturers.singular,
                        data: car?.manufacturer ? car?.manufacturer : empty,
                      },
                    ],
                    [
                      {
                        title: i18n.t.common.model,
                        data: car?.model ? car?.model : empty,
                      },
                      {
                        title: i18n.t.common.engineCapacity,
                        data: car?.displacement?.toString() ?? empty,
                      },
                    ],
                    [
                      {
                        title: i18n.t.common.hp,
                        data: car?.hp?.toString() ?? empty,
                      },
                      {
                        title: i18n.t.common.fuelType,
                        data: fuelTypes.find((x) => x.value == car?.fuelType)?.label ?? empty,
                      },
                    ],
                    [
                      {
                        title: i18n.t.plateNumbers.singular,
                        data: car?.plateNumber ? car?.plateNumber : empty,
                      },
                      {
                        title: i18n.t.highwayPasses.singular,
                        data: car?.highWayPass ? car?.highWayPass : empty,
                      },
                    ],
                  ]}
                />,
              ]}
            />
            {!isTeam && (
              <Box.Group
                boxs={[
                  <Box
                    title={i18n.t.common.carAssignment}
                    fields={[
                      [
                        {
                          title: i18n.t.common.from,
                          data: car?.driver && formatDateNumeric(car?.driver?.startDate, i18n.language),
                        },
                        {
                          title: i18n.t.common.to,
                          data: car?.driver?.endDate ? formatDateNumeric(car?.driver?.endDate, i18n.language) : empty,
                        },
                      ],
                    ]}
                  />,
                ]}
              />
            )}
            {isTeam && car?.driver ? (
              <Box.Group
                boxs={[
                  <Box
                    title={i18n.t.drivers.singular}
                    fields={[
                      [
                        { title: i18n.t.common.name, data: car?.driver?.driver.fullName },
                        {
                          title: i18n.t.common.email,
                          data: (
                            <CustomLink to={`mailto:${car?.driver?.driver.email}`}>
                              {car?.driver?.driver.email}
                            </CustomLink>
                          ),
                        },
                      ],
                      [
                        {
                          title: i18n.t.common.startDate,
                          data: formatTimeStampNumeric(car?.driver?.startDate, i18n.language),
                        },
                        {
                          title: i18n.t.common.endDate,
                          data: car.driver.endDate ? formatTimeStampNumeric(car.driver.endDate, i18n.language) : empty,
                        },
                      ],
                      [
                        {
                          title: i18n.t.common.typeOfUse,
                          data: getTypesOfUse(i18n.t).find((x) => x.value == car?.driver?.typeOfUse)?.label ?? empty,
                        },
                        null,
                      ],
                    ]}
                  />,
                ]}
              />
            ) : undefined}
            {initializedRentals && initializedRentals.total > 0 && (
              <Box.Group
                boxs={[
                  <Box title={i18n.t.rentalCars.plural}>
                    <Table<RentalCarSearch>
                      head={
                        <>
                          <th align="left">{i18n.t.plateNumbers.singular}</th>
                          <th align="left">{i18n.t.common.startDate}</th>
                          <th align="left">{i18n.t.common.endDate}</th>
                          <th></th>
                        </>
                      }
                      renderRow={(item) => (
                        <>
                          <td align="left">{item.plateNumber}</td>
                          <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
                          <td>{item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
                        </>
                      )}
                      items={initializedRentals?.items}
                    />
                  </Box>,
                ]}
              />
            )}
            {!!initializedCarDocuments?.items && initializedCarDocuments.items.length > 0 && (
              <Box.Group
                boxs={[
                  <Box title={i18n.t.documents.plural}>
                    <Table<CarDocument>
                      head={
                        <>
                          <th align="left" style={{ width: 1 }}></th>
                          <th align="left">{i18n.t.common.name}</th>
                          <th align="left">{i18n.t.common.uploadedAt}</th>
                          <th></th>
                        </>
                      }
                      renderRow={(item) => (
                        <>
                          <Table.IconTd>
                            <FileIcon fileName={item.name} />
                          </Table.IconTd>
                          <td align="left">
                            <DownloadLink url={getCarDocumentUrl(item)} fileName={item.name}>
                              {item.name}
                            </DownloadLink>
                          </td>
                          <td>{formatDateNumeric(item.uploadedAt, i18n.language)}</td>
                        </>
                      )}
                      rowActions={(item) => [getDownloadAction(i18n, () => downloadCarDocument(item))]}
                      items={initializedCarDocuments.items}
                    />
                  </Box>,
                ]}
              />
            )}
          </div>
          <div>
            <Box.Group
              boxs={[
                <Box
                  title={i18n.t.mileages.plural}
                  button={
                    showNewMileageButton()
                      ? {
                          title: i18n.t.common.addMileage,
                          onClick: () => setOpenNewMileage(true),
                        }
                      : undefined
                  }
                >
                  {initializedMileages?.total ? (
                    <Table<MileageSearch>
                      head={
                        <>
                          <th align="left">{i18n.t.common.mileage}</th>
                          <th align="left">{i18n.t.common.date}</th>
                          <th></th>
                        </>
                      }
                      renderRow={(item) => (
                        <>
                          <td align="left">
                            {!!userSettings.me.distanceInMiles
                              ? `${convertKilometersToMiles(item.distance)} ${i18n.t.common.miles}`
                              : `${item.distance} km`}
                          </td>
                          <td>{formatDateNumeric(item.date, i18n.language)}</td>
                          {!!isTeam && <td></td>}
                        </>
                      )}
                      onRowClick={(item, event) => {
                        if (!isOutPrevious7days(item.createdAt) && !isTeam) {
                          event.preventDefault();
                          setMileageToEdit(item);
                        }
                      }}
                      rowActions={
                        !isTeam
                          ? (item) => [
                              getEditAction(
                                i18n,
                                () => setMileageToEdit(item),
                                false,
                                isOutPrevious7days(item.createdAt),
                              ),
                              getDeleteAction(
                                i18n,
                                () => setMileageToDelete(item),
                                true,
                                isOutPrevious7days(item.createdAt),
                              ),
                            ]
                          : undefined
                      }
                      items={initializedMileages?.items}
                    />
                  ) : (
                    <p>{i18n.t.common.noMilegaes}</p>
                  )}
                </Box>,
              ]}
            />
            <Box.Group
              boxs={[
                <Box title={i18n.t.dueDates.plural}>
                  {initializedDueDates?.total ? (
                    <Table<DueDateSearch>
                      head={
                        <>
                          <th align="left">{i18n.t.common.type}</th>
                          <th align="left">{i18n.t.common.date}</th>
                          {isTeam && <th align="left">{i18n.t.drivers.singular}</th>}
                          <th align="left">{i18n.t.common.isSettled}</th>
                          <th align="left">{i18n.t.common.attachments}</th>
                          <th></th>
                        </>
                      }
                      renderRow={(item) => (
                        <>
                          <td>{item.type.name}</td>
                          <td align="left">{formatDateNumeric(item.date, i18n.language)}</td>
                          {isTeam && <td>{item.visibleToUser?.fullName}</td>}
                          <td>
                            {item.isSettled ? (
                              <Check icon={SvgCheck} color="green" obscured />
                            ) : isExpiringSoon(item.date) ? (
                              <Check icon={SvgCancel} color="yellow" />
                            ) : isExpired(item.date) ? (
                              <Check icon={SvgCancel} color="red" />
                            ) : undefined}
                          </td>
                          <td>
                            {item.hasAttachments && (
                              <Icon
                                className={classNames(styles.icon, styles.iconClickable)}
                                onClick={() => setShowDueDateAttachments(item)}
                                glyph={SvgAttach}
                              />
                            )}
                          </td>
                        </>
                      )}
                      rowActions={(item) => [
                        {
                          title: i18n.t.dueDates.renewSingular,
                          onClick: () => setShowRenewDueDate(item),
                          isDisabled: item.isSettled,
                          icon: SvgAutoRenew,
                        },
                      ]}
                      items={initializedDueDates.items}
                    />
                  ) : (
                    <p>{i18n.t.common.noDueDate}</p>
                  )}
                </Box>,
              ]}
            />
            {!!initializedWorkflows?.items && initializedWorkflows.items.length > 0 && (
              <Box.Group
                boxs={[
                  <Box title={i18n.t.common.tasks}>
                    <Table<WorkflowSearch>
                      head={
                        <>
                          <th align="left">{i18n.t.common.type}</th>
                          <th align="left">{i18n.t.common.action}</th>
                          <th align="left">{i18n.t.common.state}</th>
                          <th></th>
                        </>
                      }
                      rowActions={(item) => [
                        {
                          title: i18n.t.common.uploadImages,
                          onClick: () => setWorkflowToUpdate(item),
                          icon: SvgUpload,
                          visible: true,
                          isDisabled: item.stickers?.state !== "pending",
                        },
                      ]}
                      renderRow={(item) => (
                        <>
                          <td align="left">{item.carTask.taskName}</td>
                          <td>{getActionText(item)}</td>
                          <td>{getIconWorkflow(item)}</td>
                        </>
                      )}
                      items={initializedWorkflows?.items}
                    />
                  </Box>,
                ]}
              />
            )}
            {!!initializedClaims?.items && initializedClaims.items.length > 0 && (
              <Box.Group
                boxs={[
                  <Box title={i18n.t.claims.plural}>
                    <Table<ClaimSearch>
                      head={
                        <>
                          <th align="left">{i18n.t.common.type}</th>
                          <th align="left">{i18n.t.claims.incidentDate}</th>
                          <th align="left">{i18n.t.common.number}</th>
                          <th align="left">{i18n.t.common.state}</th>
                        </>
                      }
                      renderRow={(item) => (
                        <>
                          <td align="left">{item.type.name}</td>
                          <td align="left">{formatDateNumeric(item.incidentDate, i18n.language)}</td>
                          <td align="left">{item.number}</td>
                          <td>
                            <State
                              text={getClaimStatuses(i18n.t).find((x) => x.value === item.status)?.label ?? ""}
                              color={getClaimStateColor(item.status)}
                            />
                          </td>
                        </>
                      )}
                      items={initializedClaims.items}
                    />
                  </Box>,
                ]}
              />
            )}
            {!isInitialized && <LoadingSpinner />}
          </div>
        </OverviewGrid>
      </Panel>

      {!!openNewMileage && (
        <ModalDialog title={i18n.t.mileages.new} onClose={() => closeNewMileageDialog()}>
          <MileageForm carId={carId} onSubmit={onSubmitInsertMileage} carDriverId={carDriverId} />
        </ModalDialog>
      )}

      {!!mileageToEdit && (
        <EditMileageDialog
          mileageId={mileageToEdit.id}
          onClose={() => setMileageToEdit(undefined)}
          allowEdit={!isOutPrevious7days(mileageToEdit.createdAt)}
          carId={carId}
          carDriverId={carDriverId}
        />
      )}

      {!!mileageToDelete && (
        <DeleteMileageDialog
          mileageId={mileageToDelete.id}
          onClose={() => setMileageToDelete(undefined)}
          carId={carId}
          carDriverId={carDriverId}
        />
      )}

      {!!workflowToUpdate && (
        <ModalDialog onClose={closeWorkflowDialog} title={i18n.t.common.uploadImages}>
          <FileUploader
            onCancel={closeWorkflowDialog}
            allowMultiSelection
            onSave={onSubmitUploadImages}
            isSubmitting={isSubmittingUpload}
            disabled={isSubmittingUpload}
          />
        </ModalDialog>
      )}
      {!!showRenewDueDate && carId && (
        <RenewDueDatesDialog
          dueDateId={showRenewDueDate.id}
          carId={carId}
          onClose={() => setShowRenewDueDate(undefined)}
        />
      )}

      {!!showDueDateAttachments && (
        <ModalDialog title={i18n.t.common.attachments} onClose={() => setShowDueDateAttachments(undefined)}>
          <MyCarDueDateAttachments dueDateId={showDueDateAttachments.id} isTeam={isTeam ?? false} />
        </ModalDialog>
      )}
    </>
  );
};

export default OverviewMyCar;

const isExpiringSoon = (date: string): boolean => {
  const target = new Date(date);
  const today = new Date();

  // Zero out time for accurate day comparison
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays >= 0 && diffDays <= 14;
};

const isExpired = (date: string): boolean => {
  const target = new Date(date);
  const today = new Date();

  // Zero out time for accurate comparison
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return target <= today;
};
